import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import TodoNoteYearInput from '../todoNote/TodoNoteYearInput';
import {
  WD_SHORT,
  buildDueCalendarCells,
  isoFromYmd,
  parseYmd,
  tryParseMonthDayDot,
  tryParseMonthNum,
  tryParseMonthOnlyFromDayDraft,
  weekdayShortKo,
} from '../../utils/todoLikeDateInput';

export type BudgetUsageDueKind = 'day' | 'month';

export interface BudgetNoteUsageDateFieldProps {
  iso: string;
  dueKind: BudgetUsageDueKind;
  disabled?: boolean;
  onChange: (next: { iso: string; dueKind: BudgetUsageDueKind }) => void;
}

/**
 * ToDo 노트 타임테이블 날짜 셀과 동일: 연도 입력, 월.일 편집, 달력 팝오버, 월 단위 선택.
 */
const BudgetNoteUsageDateField: React.FC<BudgetNoteUsageDateFieldProps> = ({
  iso,
  dueKind,
  disabled = false,
  onChange,
}) => {
  const { y, mo, d } = parseYmd(iso);
  const wd = dueKind === 'day' ? weekdayShortKo(y, mo, d) : '';

  const [mdEditOpen, setMdEditOpen] = useState(false);
  const [mdDraft, setMdDraft] = useState('');
  const [duePickerOpen, setDuePickerOpen] = useState(false);
  const [dueCalView, setDueCalView] = useState<{ y: number; mo: number }>(() => ({ y, mo }));
  const duePickerWrapRef = useRef<HTMLDivElement>(null);
  const mdInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);
  const mdBlurSuppressed = useRef(false);

  useLayoutEffect(() => {
    if (mdEditOpen && mdInputRef.current) {
      mdInputRef.current.focus();
      mdInputRef.current.select();
    }
  }, [mdEditOpen]);

  useEffect(() => {
    setMdEditOpen(false);
  }, [dueKind]);

  useEffect(() => {
    if (disabled) setDuePickerOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (duePickerOpen) setDueCalView({ y, mo });
  }, [duePickerOpen, y, mo, iso]);

  const dueCalCells = useMemo(() => buildDueCalendarCells(dueCalView.y, dueCalView.mo), [dueCalView.y, dueCalView.mo]);

  useEffect(() => {
    if (!duePickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (duePickerWrapRef.current && !duePickerWrapRef.current.contains(e.target as Node)) {
        setDuePickerOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDuePickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [duePickerOpen]);

  const applyYear = (nextY: number) => {
    if (!Number.isFinite(nextY)) return;
    const yy = Math.min(2100, Math.max(1900, Math.round(nextY)));
    if (dueKind === 'month') onChange({ iso: isoFromYmd(yy, mo, 1), dueKind: 'month' });
    else onChange({ iso: isoFromYmd(yy, mo, d), dueKind: 'day' });
  };

  const openMdEdit = () => {
    if (disabled) return;
    setDuePickerOpen(false);
    mdBlurSuppressed.current = false;
    setMdDraft(dueKind === 'month' ? String(mo) : `${mo}.${d}`);
    setMdEditOpen(true);
  };

  const flushMdEditFromBlur = () => {
    if (mdBlurSuppressed.current) {
      mdBlurSuppressed.current = false;
      setMdEditOpen(false);
      return;
    }
    if (dueKind === 'month') {
      const full = tryParseMonthDayDot(mdDraft, y);
      if (full) {
        onChange({ iso: isoFromYmd(y, full.mo, full.d), dueKind: 'day' });
      } else {
        const mm = tryParseMonthNum(mdDraft);
        if (mm !== null) onChange({ iso: isoFromYmd(y, mm, 1), dueKind: 'month' });
      }
    } else {
      const monthOnly = tryParseMonthOnlyFromDayDraft(mdDraft);
      if (monthOnly) {
        onChange({ iso: isoFromYmd(y, monthOnly.mo, 1), dueKind: 'month' });
      } else {
        const parsed = tryParseMonthDayDot(mdDraft, y);
        if (parsed) onChange({ iso: isoFromYmd(y, parsed.mo, parsed.d), dueKind: 'day' });
      }
    }
    setMdEditOpen(false);
  };

  const cancelMdEdit = () => {
    mdBlurSuppressed.current = true;
    setMdEditOpen(false);
  };

  const onDueCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-year-line')) return;
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-cal-btn')) return;
    if ((e.target as HTMLElement).closest('.cp-todo-note-due-picker-popover')) return;
    if (duePickerOpen) {
      setDuePickerOpen(false);
      return;
    }
    if (mdEditOpen) {
      mdInputRef.current?.focus();
      return;
    }
    openMdEdit();
  };

  return (
    <div
      className={`cp-todo-note-cell cp-todo-note-cell--due${disabled ? '' : ' cp-todo-note-cell--due-hit'}`}
      onClick={onDueCellClick}
      role="presentation"
    >
      <div ref={duePickerWrapRef} className="cp-todo-note-due-cell-inner">
        <div className="cp-todo-note-due-stack">
          <div
            className="cp-todo-note-due-year-line"
            onMouseDown={(e) => {
              if (disabled) return;
              if ((e.target as HTMLElement).closest('input.cp-todo-note-due-year-input')) return;
              e.preventDefault();
              yearInputRef.current?.focus();
            }}
          >
            <div className="cp-budget-note-usage-year-inline">
              <TodoNoteYearInput year={y} disabled={disabled} onYearCommit={applyYear} inputRef={yearInputRef} aria-label="연도" />
              <span className="cp-todo-note-due-year-suffix">년</span>
            </div>
          </div>
          {mdEditOpen ? (
            <div className="cp-todo-note-due-main cp-todo-note-due-main--edit">
              <input
                ref={mdInputRef}
                type="text"
                inputMode="decimal"
                className="cp-todo-note-due-dm-input"
                disabled={disabled}
                value={mdDraft}
                onChange={(e) => setMdDraft(e.target.value)}
                onBlur={flushMdEditFromBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelMdEdit();
                  }
                }}
                placeholder="월.일"
                aria-label={
                  dueKind === 'month'
                    ? '월만: 숫자 또는 월.일 (예: 4 / 4.5)'
                    : '월.일 입력 (예: 4.29, 월만: 4 / 4. / 4.0)'
                }
              />
            </div>
          ) : dueKind === 'month' ? (
            <div className="cp-todo-note-due-main">
              <button type="button" className="cp-todo-note-due-main-hit" disabled={disabled} onClick={openMdEdit}>
                <span className="cp-todo-note-due-main-text">{mo}월</span>
              </button>
            </div>
          ) : (
            <div className="cp-todo-note-due-main">
              <button type="button" className="cp-todo-note-due-main-hit" disabled={disabled} onClick={openMdEdit}>
                <span className="cp-todo-note-due-main-text">
                  {mo}월 {d}일
                </span>
                <span className="cp-todo-note-due-weekday"> ({wd})</span>
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className="cp-todo-note-due-cal-btn"
          disabled={disabled}
          aria-label="달력에서 날짜 선택"
          aria-expanded={duePickerOpen}
          aria-haspopup="dialog"
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setDuePickerOpen((v) => !v);
          }}
        >
          <svg className="cp-todo-note-due-cal-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M8 2v4M16 2v4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 10h16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {duePickerOpen ? (
          <div
            className="cp-todo-note-due-picker-popover"
            role="dialog"
            aria-label="날짜 선택"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="cp-todo-note-due-cal-head">
              <button
                type="button"
                className="cp-todo-note-due-cal-nav"
                aria-label="이전 달"
                disabled={dueCalView.y <= 1900 && dueCalView.mo <= 1}
                onClick={() =>
                  setDueCalView((v) => {
                    const next = v.mo <= 1 ? { y: v.y - 1, mo: 12 } : { y: v.y, mo: v.mo - 1 };
                    if (next.y < 1900) return { y: 1900, mo: 1 };
                    return next;
                  })
                }
              >
                ‹
              </button>
              <span className="cp-todo-note-due-cal-title">
                {dueCalView.y}년 {dueCalView.mo}월
              </span>
              <button
                type="button"
                className="cp-todo-note-due-cal-nav"
                aria-label="다음 달"
                disabled={dueCalView.y >= 2100 && dueCalView.mo >= 12}
                onClick={() =>
                  setDueCalView((v) => {
                    const next = v.mo >= 12 ? { y: v.y + 1, mo: 1 } : { y: v.y, mo: v.mo + 1 };
                    if (next.y > 2100) return { y: 2100, mo: 12 };
                    return next;
                  })
                }
              >
                ›
              </button>
            </div>
            <div className="cp-todo-note-due-cal-weekdays" aria-hidden>
              {WD_SHORT.map((w) => (
                <span key={w} className="cp-todo-note-due-cal-wd">
                  {w}
                </span>
              ))}
            </div>
            <div className="cp-todo-note-due-cal-grid">
              {dueCalCells.map((day, idx) =>
                day === null ? (
                  <span key={`e-${idx}`} className="cp-todo-note-due-cal-cell cp-todo-note-due-cal-cell--empty" />
                ) : (
                  <button
                    key={`d-${dueCalView.y}-${dueCalView.mo}-${day}`}
                    type="button"
                    className={`cp-todo-note-due-cal-day${
                      dueKind === 'day' && y === dueCalView.y && mo === dueCalView.mo && d === day ? ' is-selected' : ''
                    }`}
                    onClick={() => {
                      onChange({ iso: isoFromYmd(dueCalView.y, dueCalView.mo, day), dueKind: 'day' });
                      setDuePickerOpen(false);
                    }}
                  >
                    {day}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className={`cp-todo-note-due-cal-monthonly${
                dueKind === 'month' && y === dueCalView.y && mo === dueCalView.mo ? ' is-active' : ''
              }`}
              onClick={() => {
                onChange({ iso: isoFromYmd(dueCalView.y, dueCalView.mo, 1), dueKind: 'month' });
                setDuePickerOpen(false);
              }}
            >
              이 달만 (월 단위 마감)
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BudgetNoteUsageDateField;
